import * as React from 'react';
import { useState } from 'react';
import { useAddToCart } from '../../hooks/useAddToCart';
import { LinkedText } from './LinkedText';
import type { WhyUsBasket, WhyUsBasketItem } from '../../types/whyus-landing';

interface WhyUsProductsProps {
  basket: WhyUsBasket;
}

export function WhyUsProducts({ basket }: WhyUsProductsProps) {
  return (
    <section className="whyus-products">
      <style>{`
        .whyus-products {
          padding: 72px 24px;
          background: #fafaf7;
        }
        .whyus-products-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .whyus-products h2 {
          font-size: 28px;
          font-weight: 800;
          color: #1a1a1a;
          margin: 0 0 8px;
          text-align: center;
        }
        .whyus-products .updated {
          text-align: center;
          font-size: 14px;
          color: #666;
          margin: 0 0 8px;
        }
        .whyus-products .honesty {
          text-align: center;
          font-size: 15px;
          color: #555;
          line-height: 1.5;
          margin: 0 auto 28px;
          max-width: 640px;
        }
        .whyus-products-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .whyus-product-card {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .whyus-product-card a {
          color: inherit;
          text-decoration: none;
        }
        .whyus-product-img {
          width: 100%;
          height: 160px;
          object-fit: cover;
          display: block;
          background: #f5f5f5;
        }
        .whyus-product-body {
          padding: 14px 16px 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }
        .whyus-product-name {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        .whyus-product-format {
          font-size: 13px;
          color: #888;
          margin: 0;
        }
        .whyus-product-price {
          font-size: 16px;
          font-weight: 700;
          color: #1EAA25;
          margin: 0;
        }
        .whyus-add-btn {
          margin-top: auto;
          border: none;
          border-radius: 8px;
          background: #1EAA25;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          padding: 8px 12px;
          cursor: pointer;
        }
        .whyus-add-btn:disabled {
          opacity: 0.7;
          cursor: default;
        }
        .whyus-pack-line {
          margin: 24px 0 0;
          text-align: center;
          font-size: 16px;
          color: #333;
        }
        .whyus-pack-line a {
          color: #1EAA25;
          font-weight: 700;
        }
        @media (max-width: 768px) {
          .whyus-products-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .whyus-product-img {
            height: 120px;
          }
          .whyus-products h2 {
            font-size: 24px;
          }
        }
      `}</style>
      <div className="whyus-products-inner">
        <h2>La canasta de hoy</h2>
        <p className="updated">{basket.updated_label}</p>
        <p className="honesty">{basket.honesty}</p>
        {basket.items.length === 0 ? (
          <p className="honesty"><LinkedText text={basket.empty} /></p>
        ) : (
          <div className="whyus-products-grid">
            {basket.items.map((item) => (
              <BasketCard key={item.id} item={item} />
            ))}
          </div>
        )}
        {basket.pack && (
          <p className="whyus-pack-line">
            <a href={basket.pack.url}>{basket.pack.line}</a>
            {' · '}
            <a href={basket.pack.pack_url}>Ver el pack</a>
          </p>
        )}
      </div>
    </section>
  );
}

function BasketCard({ item }: { item: WhyUsBasketItem }) {
  const { addToCart, loading } = useAddToCart();
  const [added, setAdded] = useState(false);

  const handleAdd = async () => {
    try {
      await addToCart(item.id, item.format_id, 1);
      setAdded(true);
      window.setTimeout(() => setAdded(false), 2000);
    } catch {
      // useAddToCart already stores the error
    }
  };

  return (
    <article className="whyus-product-card">
      <a href={item.url}>
        <img
          className="whyus-product-img"
          src={item.webp_image_url || item.image_url}
          alt={item.title}
          loading="lazy"
        />
      </a>
      <div className="whyus-product-body">
        <a href={item.url}><h3 className="whyus-product-name">{item.title}</h3></a>
        <p className="whyus-product-format">{item.format_label}</p>
        <p className="whyus-product-price">{item.price_label}</p>
        <button type="button" className="whyus-add-btn" onClick={handleAdd} disabled={loading}>
          {added ? 'Agregado' : 'Agregar'}
        </button>
      </div>
    </article>
  );
}
